import Dexie from 'dexie';
import { sharedDb } from '@monorepo/shared';

const MIGRATION_FLAG = 'nursery-species-migrated';
const RECOVERY_FLAG = 'nursery-legacy-recovery-done';

/**
 * Check if the species migration has already been completed.
 */
export function isMigrationComplete() {
  return localStorage.getItem(MIGRATION_FLAG) === 'true';
}

/**
 * Migrate existing plantSpecies from the old NurseryIrrigationTracker DB
 * to the SharedPlantTracker DB. Deduplicates by scientific name.
 * Only runs once — subsequent calls are no-ops after the flag is set.
 *
 * IMPORTANT: This must run before the app's db.js module opens the database
 * with version 4 (which drops plantSpecies). We open a separate Dexie instance
 * with the old schema to read the data before it's dropped.
 */
export async function migrateNurserySpeciesToSharedDb() {
  if (isMigrationComplete()) {
    return;
  }

  try {
    const exists = await Dexie.exists('NurseryIrrigationTracker');
    if (!exists) {
      localStorage.setItem(MIGRATION_FLAG, 'true');
      return;
    }

    // Open with the version 3 schema (the last version that had plantSpecies)
    // so we can read the data before the version 4 upgrade drops it.
    const legacyDb = new Dexie('NurseryIrrigationTracker');
    legacyDb.version(3).stores({
      plants: 'id, speciesId, gridRow, gridCol',
      logEntries: 'id, plantId, timestamp',
      photos: 'id',
      gridConfig: 'id',
      plantSpecies: 'id, commonName, scientificName',
      syncConfig: 'id',
      pendingChanges: '++seq, table, recordId, operation, timestamp',
    });

    await legacyDb.open();

    // Check if the plantSpecies table has data
    const existingSpecies = await legacyDb.plantSpecies.toArray();

    if (existingSpecies.length === 0) {
      legacyDb.close();
      localStorage.setItem(MIGRATION_FLAG, 'true');
      return;
    }

    for (const species of existingSpecies) {
      if (!species.scientificName) continue;

      const alreadyExists = await sharedDb.plantSpecies
        .where('scientificName')
        .equalsIgnoreCase(species.scientificName)
        .first();

      if (!alreadyExists) {
        await sharedDb.plantSpecies.put(species);
      }
    }

    legacyDb.close();
  } catch (error) {
    // If migration fails (e.g., old DB doesn't exist or table is gone),
    // log the error but don't block the app from starting.
    console.warn('Species migration encountered an error:', error);
  }

  // Mark migration as complete regardless of partial failures
  localStorage.setItem(MIGRATION_FLAG, 'true');
}

/**
 * Check if legacy S3 recovery is needed.
 * Returns true if recovery hasn't been done yet.
 */
export function needsLegacyRecovery() {
  return localStorage.getItem(RECOVERY_FLAG) !== 'true';
}

/**
 * Mark legacy recovery as complete.
 */
export function markLegacyRecoveryDone() {
  localStorage.setItem(RECOVERY_FLAG, 'true');
}
